import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { MenuIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, LayoutDashboardIcon, PillIcon, PackageCheckIcon, ClipboardListIcon, TruckIcon, SlidersHorizontalIcon, ArrowLeftRightIcon, CalendarX2Icon, AlertTriangleIcon, BarChart3Icon, HistoryIcon, FileTextIcon } from 'lucide-react';
import { PharmacyOverview } from './PharmacyOverview';
import { PharmacistDashboard } from './PharmacistDashboard';
import { InventoryList } from './InventoryList';
import { ReceiveStock } from './ReceiveStock';
import { PurchaseOrders } from './PurchaseOrders';
import { Suppliers } from './Suppliers';
import { StockActions } from './StockActions';
import { ExpiredMedicines } from './ExpiredMedicines';
import { LowStockAlerts } from './LowStockAlerts';
import { InventoryReports } from './InventoryReports';
import { StockMovementHistory } from './StockMovementHistory';
import { PharmacyDispensing } from './PharmacyDispensing';
import { expiryAlertWindow, isLowStockOnly, isOutOfStock } from './pharmacyUtils';
export type PharmacySection = 'overview' | 'dispensing' | 'inventory' | 'receive-stock' | 'purchase-orders' | 'suppliers' | 'adjustments' | 'returns' | 'expired' | 'low-stock' | 'reports' | 'movement-history';
type NavItem = {
  id: PharmacySection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
  tone?: 'red' | 'amber' | 'blue';
};
type NavGroup = {
  label: string;
  items: NavItem[];
};
export function PharmacyModule() {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders
  } = useData();
  const dispenseOnly = currentUser?.role === 'Pharmacy';
  const [section, setSection] = useState<PharmacySection>(dispenseOnly ? 'dispensing' : 'overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const alerts = useMemo(() => ({
    low: medicines.filter(isLowStockOnly).length,
    out: medicines.filter(isOutOfStock).length,
    expiring: medicineBatches.filter(batch => expiryAlertWindow(batch) !== null).length,
    purchaseOrders: purchaseOrders.filter(order => order.status === 'Pending').length
  }), [medicineBatches, medicines, purchaseOrders]);
  if (!currentUser || !['Administrator', 'Pharmacy', 'Pharmacy Manager'].includes(currentUser.role)) return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-5300" data-mp="2otzxorb23qp">
        You do not have permission to access the Pharmacy workspace.
      </div>;
  const readOnly = currentUser.role === 'Administrator';
  const manager = currentUser.role === 'Pharmacy Manager';
  const managerGroups: NavGroup[] = [{
    label: 'Pharmacy',
    items: [{
      id: 'overview',
      label: 'Pharmacist Dashboard',
      icon: LayoutDashboardIcon
    }, {
      id: 'dispensing',
      label: 'Dispensing',
      icon: ClipboardListIcon
    }, {
      id: 'inventory',
      label: 'Inventory',
      icon: PillIcon
    }, {
      id: 'receive-stock',
      label: 'Receive New Stock',
      icon: PackageCheckIcon
    }, {
      id: 'adjustments',
      label: 'Stock Adjustments',
      icon: SlidersHorizontalIcon
    }, {
      id: 'returns',
      label: 'Medicine Returns',
      icon: ArrowLeftRightIcon
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      icon: CalendarX2Icon,
      count: alerts.expiring,
      tone: medicineBatches.some(batch => expiryAlertWindow(batch) === 'Expired') ? 'red' : 'amber'
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      icon: AlertTriangleIcon,
      count: alerts.low + alerts.out + alerts.expiring,
      tone: alerts.out ? 'red' : 'amber'
    }]
  }, {
    label: 'Procurement',
    items: [{
      id: 'purchase-orders',
      label: 'Purchase Orders',
      icon: FileTextIcon,
      count: alerts.purchaseOrders,
      tone: 'blue'
    }, {
      id: 'suppliers',
      label: 'Suppliers',
      icon: TruckIcon
    }]
  }, {
    label: 'Insights',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      icon: HistoryIcon
    }]
  }];
  const groups: NavGroup[] = readOnly ? [{
    label: 'Monitoring',
    items: [{
      id: 'overview',
      label: 'Pharmacy Overview',
      icon: LayoutDashboardIcon
    }, {
      id: 'inventory',
      label: 'Pharmacy Inventory',
      icon: PillIcon
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      icon: AlertTriangleIcon,
      count: alerts.low + alerts.out + alerts.expiring,
      tone: alerts.out ? 'red' : 'amber'
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      icon: CalendarX2Icon,
      count: alerts.expiring,
      tone: medicineBatches.some(batch => expiryAlertWindow(batch) === 'Expired') ? 'red' : 'amber'
    }]
  }, {
    label: 'Audit & reports',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      icon: HistoryIcon
    }]
  }] : manager ? managerGroups : [{
    label: 'Dispensing desk',
    items: [{
      id: 'dispensing',
      label: 'Dispensing',
      icon: ClipboardListIcon
    }]
  }];
  const select = (next: PharmacySection) => {
    setSection(next);
    setMobileOpen(false);
  };
  const content = () => {
    if (dispenseOnly) return <PharmacyDispensing data-id="element-5301" data-mp="wwndmjk2nqd4" />;
    switch (section) {
      case 'overview':
        return manager ? <PharmacistDashboard onNavigate={select} data-id="element-5302" data-mp="10xz22lr7m5jq" /> : <PharmacyOverview onNavigate={select} readOnly={readOnly} data-id="element-5303" data-mp="n12fwxm1dc96" />;
      case 'dispensing':
        return <PharmacyDispensing onBack={() => select('overview')} data-id="element-5304" data-mp="ryvjdivyfs5r" />;
      case 'inventory':
        return <InventoryList readOnly={readOnly} data-id="element-5305" data-mp="8ede2p4bamzm" />;
      case 'receive-stock':
        return <ReceiveStock data-id="element-5306" data-mp="3g27qod86um5" />;
      case 'purchase-orders':
        return <PurchaseOrders data-id="element-5307" data-mp="ibrmc1g305gu" />;
      case 'suppliers':
        return <Suppliers data-id="element-5308" data-mp="idcghpyhhme6" />;
      case 'adjustments':
        return <StockActions mode="adjustment" data-id="element-5309" data-mp="dceypehdyud7" />;
      case 'returns':
        return <StockActions mode="return" data-id="element-5310" data-mp="dfax13x5bl26" />;
      case 'expired':
        return <ExpiredMedicines data-id="element-5311" data-mp="rb4y50roh64q" />;
      case 'low-stock':
        return <LowStockAlerts data-id="element-5312" data-mp="me66covhlb9p" />;
      case 'reports':
        return <InventoryReports data-id="element-5313" data-mp="mbjy0n0rurqf" />;
      case 'movement-history':
        return <StockMovementHistory data-id="element-5314" data-mp="111hhwqro4v9t" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Pharmacy navigation" data-id="element-5315" data-mp="w364y762u4nd">
      {groups.map(group => <div key={group.label} data-id="element-5316" data-mp="1a2fl56ua8yrc">
          <p className={`chrome-label mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] ${collapsed && !mobile ? 'sr-only' : ''}`} data-id="element-5317" data-mp="1a3qmz1463wft">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-5318" data-mp="2cgblbdmcrqjf">
            {group.items.map(item => {
          const Icon = item.icon;
          const active = section === item.id;
          return <button key={item.id} type="button" onClick={() => select(item.id)} title={collapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium ${active ? 'chrome-nav-item-active' : 'chrome-nav-item'} ${collapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-5319" data-mp="2mf95k4cqwxwm">
                  <Icon className={`h-4 w-4 shrink-0 ${collapsed && !mobile ? '' : 'mr-3'}`} data-id="element-5320" data-mp="27h8epoqq5c9c" />
                  {(!collapsed || mobile) && <>
                      <span className="truncate" data-id="element-5321" data-mp="1f0xmvimxru0m">{item.label}</span>
                      {item.count ? <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${item.tone === 'red' ? 'bg-red-100 text-red-700' : item.tone === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-5322" data-mp="17i6k9fypxrxq">
                          {item.count}
                        </span> : null}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-5323" data-mp="jq997r33f3xo">
      <aside className={`chrome-sidebar chrome-divider hidden shrink-0 border-r lg:flex lg:flex-col ${collapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-5324" data-mp="et969p1jihas">
        <div className={`chrome-divider border-b ${collapsed ? 'p-3' : 'p-5'}`} data-id="element-5325" data-mp="5rjcick6r69u">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`} data-id="element-5326" data-mp="tzyndregvif">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-chrome-active-ink" data-id="element-5327" data-mp="tlpv7uu24hee">
              <PillIcon className="h-5 w-5" data-id="element-5328" data-mp="13dm6k7l5m40k" />
            </div>
            {!collapsed && <div data-id="element-5329" data-mp="13fgkv31x6xvo">
                <p className="text-sm font-bold" data-id="element-5330" data-mp="ye0vwzwmg780">BMED Care</p>
                <p className="chrome-label text-xs font-medium" data-id="element-5331" data-mp="ygm0vxs7v39z">
                  {manager ? 'Pharmacist' : dispenseOnly ? 'Dispensing Desk' : 'Pharmacy Operations'}
                </p>
              </div>}
          </div>
          {!collapsed && <span className="mt-4 inline-flex rounded-md bg-white/20 px-2.5 py-1 text-[11px] font-semibold" data-id="element-5332" data-mp="fnrvnvkeapiy">
              {manager ? 'Administrative rights' : dispenseOnly ? 'Dispensing & prescription forms' : 'Live inventory ledger'}
            </span>}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-5333" data-mp="38zibalf6fzcu">{navigation()}</div>
        <div className="chrome-divider border-t p-3" data-id="element-5334" data-mp="3e0pjump592kk">
          <button type="button" onClick={() => setCollapsed(value => !value)} className="chrome-icon-button flex w-full items-center justify-center rounded-md py-2" aria-label={collapsed ? 'Expand pharmacy navigation' : 'Collapse pharmacy navigation'} data-id="element-5335" data-mp="300eq9bwg99vp">
            {collapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-5336" data-mp="3iz96yqfh4kei" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-5337" data-mp="3ix6fs32l4l87" />}
          </button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-5338" data-mp="fhr0ambftjfo">
          <button type="button" aria-label="Close pharmacy navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setMobileOpen(false)} data-id="element-5339" data-mp="fgftseqlxxkv" />
          <aside className="chrome-sidebar relative flex h-full w-[280px] flex-col shadow-2xl" data-id="element-5340" data-mp="p5wi4zy4p6zi">
            <div className="chrome-divider flex items-center justify-between border-b p-5" data-id="element-5341" data-mp="k6tdvhpz34sl">
              <div data-id="element-5342" data-mp="k8mmh2k5bz38">
                <p className="text-sm font-bold" data-id="element-5343" data-mp="f7ytibccx86d">BMED Care</p>
                <p className="chrome-label text-xs font-medium" data-id="element-5344" data-mp="f9t714laty0f">
                  {manager ? 'Pharmacist' : dispenseOnly ? 'Dispensing Desk' : 'Pharmacy Operations'}
                </p>
              </div>
              <button type="button" onClick={() => setMobileOpen(false)} className="chrome-icon-button rounded-md p-2" aria-label="Close pharmacy navigation" data-id="element-5345" data-mp="1glffcx7x2iq">
                <XIcon className="h-5 w-5" data-id="element-5346" data-mp="3jm6tmuuy3n8h" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-5347" data-mp="3joawd9h3yg76">{navigation(true)}</div>
          </aside>
        </div>}
      <section className="min-w-0 flex-1" data-id="element-5348" data-mp="2357t2ntwpi5h">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-5349" data-mp="233wnqjktc2c9">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-5350" data-mp="282hn6gs8qhzn">
            <div className="flex min-w-0 items-center gap-3" data-id="element-5351" data-mp="2d1kvqb7fj6cd">
              <button type="button" onClick={() => setMobileOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open pharmacy navigation" data-id="element-5352" data-mp="1fgy8tqccd2gg">
                <MenuIcon className="h-5 w-5" data-id="element-5353" data-mp="1u52vf5eily4i" />
              </button>
              <div data-id="element-5354" data-mp="wpf8xsdm64in">
                <p className="text-xs font-medium text-slate-500" data-id="element-5355" data-mp="wpxddcafjp1n">
                  {manager ? 'Pharmacist' : 'Pharmacy'}{' '}
                  <span className="mx-1 text-slate-300" data-id="element-5356" data-mp="11n7bdgbvx913">/</span>{' '}
                  {dispenseOnly ? 'Dispensing' : 'Operations'}
                </p>
                <h1 className="mt-0.5 text-lg font-bold text-slate-900 sm:text-xl" data-id="element-5357" data-mp="2zc2s05sgecav">
                  {groups.flatMap(group => group.items).find(item => item.id === section)?.label}
                </h1>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-5358" data-mp="3n0voq4gwgo6j">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-5359" data-mp="3i3dhciij6h0n" /> Live
              data
            </span>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-5360" data-mp="3w08yyzculgsb">
          {content()}
        </main>
      </section>
    </div>;
}